import { setMasterPlaybackVolumeFactor } from '@src/utility/masterPlaybackVolume';
import parsePercent from '@src/utility/parsePercent';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ElementState } from '@src/renderer/ElementState';

/**
 * The slice of the Creatomate renderer bridge this hook needs. Structural so
 * both `Renderer`/`WarmedRenderer` and test doubles satisfy it.
 */
export interface VolumeCapableRenderer {
  ready: boolean;
  getElements: () => ElementState[];
  setModifications: (modifications: Record<string, any>) => Promise<void>;
  setActiveElements: (elementIds: string[]) => Promise<void>;
  setActiveComposition: (elementId: string | null) => Promise<void>;
  play: () => Promise<void>;
  pause: () => Promise<void>;
}

/** Player-level volume state + actions consumed by `PlayerVolumeControl`. */
export interface PlayerVolumeControls {
  /** Slider position 0..1. Preserved while muted so unmute restores it. */
  volume: number;
  /** Display-muted: explicitly muted OR slider dragged to 0. */
  muted: boolean;
  /**
   * Set the slider position. Uncommitted calls (drag in progress) update the
   * UI immediately and push to the renderer on a trailing throttle without an
   * audio-buffer flush; `commit: true` (drag release, keyboard step) applies
   * immediately and flushes so the change is audible right away.
   */
  setVolume: (volume: number, opts?: { commit?: boolean }) => void;
  toggleMute: () => void;
}

interface UsePlayerVolumeParams {
  /**
   * Whether renderer pushes are currently allowed — the renderer is the
   * visible player, ready, and not mid-reload. While false, interactions only
   * update React state (e.g. the modal's Published mode, which is driven by
   * Mux props instead); the reconcile effect re-pushes when it flips true.
   */
  enabled: boolean;
  /** Live accessor for the renderer this control drives. */
  getRenderer: () => VolumeCapableRenderer | null;
  /** Current canvas selection, re-applied by the audio-buffer flush. */
  getActiveElementIds: () => string[];
  /** Whether the renderer is currently playing. */
  isPlaying: boolean;
  /**
   * Optional extra reconcile trigger: while `enabled`, a change re-pushes a
   * non-default factor (e.g. pass `isPlaying` where play-start may follow a
   * source refresh that reset modifications).
   */
  reconcileToken?: unknown;
}

/** Trailing-throttle window for renderer pushes while the slider is dragged. */
const DRAG_THROTTLE_MS = 150;

const AUDIO_ELEMENT_TYPES = new Set(['video', 'audio']);

const roundPercent = (value: number) => Math.round(value * 10) / 10;

/**
 * Master volume/mute for a Creatomate-backed player surface — used by both
 * the Creator Studio stage and the Content Studio clip preview so the control
 * behaves identically everywhere.
 *
 * The Creatomate bridge has no player-level volume API, so the master factor
 * is fanned out as per-element `<id>.volume` modifications (effective =
 * authored volume × factor) over every audio-bearing element —
 * `setModifications` never mutates the source, so authored volumes are
 * untouched. Creatomate keeps a few seconds of audio pre-buffered at the old
 * volume; committed changes therefore re-apply the selection and, when
 * playing, toggle pause/play to flush that buffer (same recipe as
 * `VideoCreatorStore.flushRendererAudioBuffer`).
 *
 * The factor is mirrored into `masterPlaybackVolume` so VideoCreatorStore's
 * own volume pushes (key-frame playback loop, `applyVolumeForElement`) scale
 * by it instead of stomping a player-level mute.
 *
 * State is host-local: on unmount authored volumes are restored and the
 * master factor resets to 1, so nothing leaks to the next surface (the
 * prewarmed clip-preview renderer outlives its modal).
 */
export function usePlayerVolume(
  params: UsePlayerVolumeParams,
): PlayerVolumeControls {
  const { enabled, getRenderer, getActiveElementIds, isPlaying } = params;

  const [volume, setVolumeState] = useState(1);
  const [muted, setMutedState] = useState(false);

  // Refs mirror props/state so the imperative callbacks stay identity-stable
  // (the unmount-restore effect depends on them) and never read stale values.
  const volumeRef = useRef(volume);
  const mutedRef = useRef(muted);
  const enabledRef = useRef(enabled);
  const isPlayingRef = useRef(isPlaying);
  const getRendererRef = useRef(getRenderer);
  const getActiveElementIdsRef = useRef(getActiveElementIds);
  enabledRef.current = enabled;
  isPlayingRef.current = isPlaying;
  getRendererRef.current = getRenderer;
  getActiveElementIdsRef.current = getActiveElementIds;

  /** Restore target when unmuting after the slider was dragged to 0. */
  const lastAudibleVolumeRef = useRef(1);
  /**
   * Authored per-element volume percents, captured first-seen-wins before any
   * modification is pushed, so restore is always exact.
   */
  const originalVolumesRef = useRef(new Map<string, number>());
  /** Last factor actually pushed to the renderer — drives reconciliation. */
  const rendererFactorRef = useRef(1);

  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFactorRef = useRef<number | null>(null);

  const effectiveFactor = (m: boolean, v: number) => (m ? 0 : v);

  /**
   * Fan the master factor out to every audio-bearing element. No `enabled`
   * guard — cleanup must be able to restore a renderer that is no longer the
   * visible player (e.g. the modal closed from Published mode).
   */
  const pushVolumes = useCallback(
    (factor: number, opts?: { flush?: boolean }) => {
      setMasterPlaybackVolumeFactor(factor);
      const renderer = getRendererRef.current();
      if (!renderer?.ready) return;

      const modifications: Record<string, string> = {};
      for (const element of renderer.getElements()) {
        const id = element.source?.id;
        const type = element.source?.type;
        if (!id || !AUDIO_ELEMENT_TYPES.has(type)) continue;
        let original = originalVolumesRef.current.get(id);
        if (original === undefined) {
          original = parsePercent(element.source.volume) ?? 100;
          originalVolumesRef.current.set(id, original);
        }
        modifications[`${id}.volume`] = `${roundPercent(original * factor)}%`;
      }
      if (Object.keys(modifications).length === 0) return;

      renderer.setModifications(modifications).catch(() => {});
      rendererFactorRef.current = factor;

      if (opts?.flush) {
        // Creatomate flushes its audio buffer on selection re-apply and on
        // pause/play, so force a re-fetch at the new volume.
        renderer
          .setActiveElements(getActiveElementIdsRef.current())
          .catch(() => {});
        renderer.setActiveComposition(null).catch(() => {});
        if (isPlayingRef.current) {
          renderer.pause().catch(() => {});
          renderer.play().catch(() => {});
        }
      }
    },
    [],
  );

  /** `pushVolumes`, but only while this renderer is the enabled player. */
  const applyMasterVolume = useCallback(
    (factor: number, opts?: { flush?: boolean }) => {
      if (!enabledRef.current) return;
      pushVolumes(factor, opts);
    },
    [pushVolumes],
  );

  const cancelThrottle = useCallback(() => {
    if (throttleTimerRef.current !== null) {
      clearTimeout(throttleTimerRef.current);
      throttleTimerRef.current = null;
    }
    pendingFactorRef.current = null;
  }, []);

  const scheduleThrottledApply = useCallback(
    (factor: number) => {
      pendingFactorRef.current = factor;
      if (throttleTimerRef.current !== null) return;
      throttleTimerRef.current = setTimeout(() => {
        throttleTimerRef.current = null;
        const pending = pendingFactorRef.current;
        pendingFactorRef.current = null;
        if (pending !== null) applyMasterVolume(pending);
      }, DRAG_THROTTLE_MS);
    },
    [applyMasterVolume],
  );

  const setVolume = useCallback(
    (raw: number, opts?: { commit?: boolean }) => {
      const next = Math.min(1, Math.max(0, raw));
      setVolumeState(next);
      volumeRef.current = next;
      if (next > 0) {
        lastAudibleVolumeRef.current = next;
        if (mutedRef.current) {
          // Dragging the slider up from muted unmutes.
          setMutedState(false);
          mutedRef.current = false;
        }
      }
      const factor = effectiveFactor(mutedRef.current, next);
      if (opts?.commit) {
        cancelThrottle();
        applyMasterVolume(factor, { flush: true });
      } else {
        scheduleThrottledApply(factor);
      }
    },
    [applyMasterVolume, cancelThrottle, scheduleThrottledApply],
  );

  const toggleMute = useCallback(() => {
    const displayMuted = mutedRef.current || volumeRef.current === 0;
    cancelThrottle();
    if (displayMuted) {
      setMutedState(false);
      mutedRef.current = false;
      let next = volumeRef.current;
      if (next === 0) {
        next = lastAudibleVolumeRef.current || 1;
        setVolumeState(next);
        volumeRef.current = next;
      }
      applyMasterVolume(next, { flush: true });
    } else {
      setMutedState(true);
      mutedRef.current = true;
      applyMasterVolume(0, { flush: true });
    }
  }, [applyMasterVolume, cancelThrottle]);

  // Reconcile whenever the renderer (re)becomes the enabled player (mode
  // switches, source reloads that may reset modifications) or the token
  // changes. A non-1 factor is always re-pushed — idempotent when already
  // applied.
  useEffect(() => {
    if (!enabled) return;
    const factor = effectiveFactor(mutedRef.current, volumeRef.current);
    if (factor !== 1 || rendererFactorRef.current !== 1) {
      pushVolumes(factor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, params.reconcileToken, pushVolumes]);

  // On unmount, restore authored volumes and reset the master factor: the
  // prewarmed clip-preview renderer outlives its modal, so a leftover mute
  // would silence the next session. Pushing the originals (factor 1) is safe
  // regardless of how the embed merges modification objects.
  useEffect(
    () => () => {
      cancelThrottle();
      if (rendererFactorRef.current !== 1) {
        pushVolumes(1);
      }
      setMasterPlaybackVolumeFactor(1);
    },
    [cancelThrottle, pushVolumes],
  );

  return {
    volume,
    muted: muted || volume === 0,
    setVolume,
    toggleMute,
  };
}
